/*
    Admin users management page
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import styles from '../../style/info-card.module.css';
import usersStyles from '../../style/admin/users-page.module.css';
import Table from '../table';
import { Checkbox, notification, Radio } from 'antd';
import history from '../../history';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US');
};

const UsersPage = () => {
  const token = useSelector((state) => state.token);
  const userInfo = useSelector((state) => state.userInfo);
  const [users, setUsers] = useState([]);
  const [approvedFilter, setApprovedFilter] = useState('all');
  const [pendingApprovals, setPendingApprovals] = useState({});

  // Redirect non-admin users
  useEffect(() => {
    if (userInfo.kind && userInfo.kind !== 'Administrator') {
      history.push('/');
    }
  }, [userInfo.kind]);

  const getUsers = useCallback(async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/user/directory', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      notification.error({
        message: 'Failed to load users',
        description: error.message
      });
    }
  }, [token]);

  const approveUser = useCallback(
    async (userID, approved) => {
      setPendingApprovals((prev) => ({ ...prev, [userID]: true }));

      try {
        await axios.post(
          process.env.REACT_APP_API_URL + '/user/approve',
          { userID, approved },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Token ' + token
            }
          }
        );

        setUsers((prev) =>
          prev.map((user) => (user._id === userID ? { ...user, approved } : user))
        );

        if (approved) {
          notification.success({
            message: 'User approved',
            description: 'The user has been notified by email.'
          });
        } else {
          notification.success({
            message: 'User approval revoked'
          });
        }
      } catch (error) {
        console.error('Failed to update user approval:', error);
        notification.error({
          message: 'Failed to update approval',
          description: error.message
        });
      } finally {
        setPendingApprovals((prev) => ({ ...prev, [userID]: false }));
      }
    },
    [token]
  );

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const columns = [
    { dataKey: 'firstName', title: 'First Name', type: 'input' },
    { dataKey: 'lastName', title: 'Last Name', type: 'input' },
    { dataKey: 'email', title: 'Email', type: 'input' },
    { dataKey: 'createdAt', title: 'Created', type: 'input' },
    { dataKey: 'approved', title: 'Approved', type: 'node' }
  ];

  const filteredUsers = users.filter((user) => {
    if (approvedFilter === 'approved') return user.approved;
    if (approvedFilter === 'unapproved') return !user.approved;
    return true;
  });

  const data = filteredUsers.map((user) => ({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    createdAt: formatDate(user.createdAt),
    approved: (
      <Checkbox
        checked={user.approved}
        disabled={!!pendingApprovals[user._id]}
        onChange={(e) => approveUser(user._id, e.target.checked)}
      />
    )
  }));

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['header']}>
          <label className={styles['name']}>Users</label>
        </div>
        <hr className={styles['horizontal-line']}></hr>
        <div className={usersStyles.filterContainer}>
          <Radio.Group value={approvedFilter} onChange={(e) => setApprovedFilter(e.target.value)}>
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="approved">Approved</Radio.Button>
            <Radio.Button value="unapproved">Unapproved</Radio.Button>
          </Radio.Group>
        </div>
        <div className={`${styles['info-container']} ${usersStyles.tableContainer}`}>
          <Table
            canAdd={false}
            canDelete={false}
            canModify={false}
            columns={columns}
            data={data}
            isEditing={false}
          />
        </div>
      </div>
    </Dashboard>
  );
};

export default UsersPage;
